<template>
  <v-flex xs12>
    <v-toolbar flat>
      <v-toolbar-title>Mitarbeiter</v-toolbar-title>
      <v-spacer />

      <!-- edit -->
      <v-dialog
        v-model="showEditDialog"
        :max-width="dialogWidth"
        persistent
      >
        <v-card>
          <v-card-title>
            <span
              v-if="selectedObject.id !== null"
              class="headline"
            >Bearbeiten: {{ getEmployeeLabel(selectedObject) }}</span>
            <span
              v-else-if="!!getEmployeeLabel(selectedObject)"
              class="headline"
            >Neuer Mitarbeiter: {{ getEmployeeLabel(selectedObject) }}</span>
            <span
              v-else
              class="headline"
            >Neuer Mitarbeiter</span>
          </v-card-title>
          <v-card-text>
            <v-form @submit.prevent="submitEdit">
              <v-text-field
                v-model="selectedObject.user.username"
                type="text"
                label="Benutzername"
                :disabled="saving"
                :error-messages="getError('user.username')"
              />
              <v-row>
                <v-col cols="12" md="6">
                   <v-text-field
                     v-model="selectedObject.user.first_name"
                      type="text"
                      label="Vorname"
                      :disabled="saving"
                      :error-messages="getError('user.first_name')"
                    />
                </v-col>
                <v-col cols="12" md="6">
                   <v-text-field
                    v-model="selectedObject.user.last_name"
                    type="text"
                    label="Nachname"
                    :disabled="saving"
                    :error-messages="getError('user.last_name')"
                  />
                </v-col>
              </v-row>
              <v-text-field
                v-model="selectedObject.user.email"
                type="email"
                label="E-Mail-Adresse"
                :disabled="saving"
                :error-messages="getError('user.email')"
              />

              <v-checkbox
                v-model="selectedObject.changePassword"
                label="Kennwort ändern"
                :disabled="saving || selectedObject.id === null"
              />
              <v-expand-transition>
                <div v-if="selectedObject.changePassword">
                  <v-text-field
                    v-model="selectedObject.user.password"
                    type="password"
                    label="Kennwort"
                    :disabled="saving"
                    :error-messages="getError('user.password')"
                  />
                  <v-text-field
                    v-model="selectedObject.user.password2"
                    type="password"
                    label="Kennwort wiederholen"
                    :disabled="saving"
                    :error-messages="errors.password"
                  />
                </div>
              </v-expand-transition>
              <v-checkbox
                v-model="selectedObject.inactive"
                label="Inaktiv"
                :disabled="saving"
                :error-messages="getError('inactive')"
              />
              <Multiselect
                v-model="selectedObject.projects"
                label="name"
                multiple
                :options="allProjects"
                track-by="id"
                :close-on-select="false"
                placeholder="Projekte auswählen"
                select-label="Mit Eingabe-Taste auswählen"
                selected-label="Ausgewählt"
                deselect-label="Mit Eingabe-Taste abwählen"
                :disabled="ignore_project_operation_assignment"
              >
                <template v-slot:noResult>
                  Keine Suchergebnisse.
                </template>
                <template v-slot:noOptions>
                  Liste ist leer.
                </template>
              </Multiselect>

              <span v-if="ignore_project_operation_assignment">
                <b>Projekte und Tätigkeiten werden automatisch allen Mitarbeitern angezeigt.</b>
              </span>

              <v-checkbox
                v-model="selectedObject.group_leader"
                label="Gruppenleiter"
                :disabled="saving"
              />
              <v-expand-transition>
                <div v-if="selectedObject.group_leader">
                  <Multiselect
                    v-model="selectedObject.manages_employees"
                    :customLabel="getEmployeeLabel"
                    multiple
                    :options="allEmployees"
                    track-by="id"
                    :close-on-select="false"
                    placeholder="Geleitete Mitarbeiter auswählen"
                    select-label="Mit Eingabe-Taste auswählen"
                    select-group-label="Alle Einträge in der Gruppe auswählen"
                    selected-label="Ausgewählt"
                    deselect-label="Mit Eingabe-Taste abwählen"
                    deselect-group-label="Alle Einträge in der Gruppe abwählen"
                    group-values="values"
                    group-label="label"
                    :group-select="true"
                  >
                    <template v-slot:noResult>
                      Keine Suchergebnisse.
                    </template>
                    <template v-slot:noOptions>
                      Liste ist leer.
                    </template>
                  </Multiselect>
                </div>
              </v-expand-transition>
              <v-checkbox
                v-model="selectedObject.is_tenant_admin"
                label="Mandantenweiter Administrator"
                :disabled="saving || !canChangeTenantAdminField"
              />
            </v-form>
            <v-progress-linear
              v-if="saving"
              indeterminate
            />
            <v-alert type="error" :value="!!errors.validation.detail">
              {{ errors.validation.detail }}
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="saving"
              @click="$router.push({name: 'employees'})"
            >
              Abbrechen
            </v-btn>
            <v-btn
              text
              :disabled="saving"
              @click="saveEditDialog"
            >
              Speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- confirm removal -->
      <v-dialog
        v-model="showDeleteDialog"
        :max-width="dialogWidth"
      >
        <v-card>
          <v-card-title><span class="headline">Mitarbeiter wirklich löschen?</span></v-card-title>
          <v-card-text>
            <p>Möchten Sie den Mitarbeiter <em>{{ getEmployeeLabel(selectedObject) }}</em> wirklich löschen?</p>
            <p>Achtung: Dadurch werden alle Stundeneinträge, die diesem Mitarbeiter zugeordnet sind, unwiderruflich gelöscht.</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn
              text
              :disabled="saving"
              @click="showDeleteDialog = false"
            >
              Abbrechen
            </v-btn>
            <v-btn
              text
              :disabled="saving"
              @click="deleteEmployee"
            >
              Löschen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-toolbar-items>
        <v-btn
          icon
          :disabled="loading"
          @click="fetchItems"
        >
          <v-icon>mdi-cached</v-icon>
        </v-btn>
        <v-btn
          icon
          :disabled="loading"
          @click="openNewDialog"
        >
          <v-icon>mdi-plus</v-icon>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <!-- actual data -->
    <v-data-table
      :headers="headers"
      :items-per-page="-1"
      :items="items"
      :loading="loading"
      fixed-header
      :height="dataTableHeight"
    >
      <template v-slot:item="props">
        <tr>
          <td>{{ props.item.user.first_name }}</td>
          <td>{{ props.item.user.last_name }}</td>
          <td>{{ props.item.user.username }}</td>
          <td>{{ props.item.user.email }}</td>
          <td>
            <v-icon
              v-if="props.item.inactive"
              small
            >
              {{ icons.yes }}
            </v-icon>
            <v-icon
              v-else
              small
            >
              {{ icons.no }}
            </v-icon>
          </td>
          <td>
            <v-icon v-if="isGroupLeader(props.item)" small>
              {{ icons.yes }}
            </v-icon>
            <v-icon v-else small>
              {{ icons.no }}
            </v-icon>
          </td>
          <td>
            <v-icon v-if="isTenantAdmin(props.item, selectedTenant) || props.item.is_superuser" small>
              {{ icons.yes }}
            </v-icon>
            <v-icon v-else small>
              {{ icons.no }}
            </v-icon>
          </td>
          <td class="text-right">
            <v-btn
              icon
              title="Bearbeiten"
              :disabled="loading"
              @click="openEditDialog(props.item)"
            >
              <v-icon small>
                mdi-pencil
              </v-icon>
            </v-btn>
            <v-btn
              icon
              title="Löschen"
              :disabled="loading"
              @click="openDeleteDialog(props.item)"
            >
              <v-icon small>
                mdi-delete
              </v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
      <template v-slot:no-data>
        <v-alert type="error" :value="errors.unknown">{{ errors.unknown }}</v-alert>
        <span>Keine Daten.</span>
      </template>
    </v-data-table>
  </v-flex>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import { api, endpoints } from '@/api'
import Multiselect from 'vue-multiselect'

import {getEmployeeLabel} from '../filters/Employee'

import {dialogWidth,dataTableHeight} from './utils/sizes'

export default {
  name: 'Management2',
  metaInfo: {
    title: 'Mitarbeiter verwalten',
  },
  components: {
    Multiselect,
  },
  data () {
    return {
      loading: false,
      saving: false,
      items: [],
      selectedItem: null,
      showEmptyElement: false,
      headers: [
        { text: "Vorname", value: "user.first_name" },
        { text: "Nachname", value: "user.last_name" },
        { text: "Benutzername", value: "user.username" },
        { text: "E-Mail-Adresse", value: "user.email" },
        { text: "Inaktiv", value: "inactive", sortable: false },
        { text: "Gruppenleiter", value: "id", sortable: false },
        { text: "Admin", value: "id", sortable: false },
        { text: "Aktion", value: "id", sortable: false, class: "text-right" },
      ],
      showEditDialog: false,
      showDeleteDialog: false,
      selectedObject: {
        id: null,
        name: "",
        inactive: false,
        changePassword: false,
        user: {
          username: "",
          email: "",
          password: "",
          password2: "",
        },
        projects: [],
        manages_employees: [],
        group_leader: false,
        is_tenant_admin: false,
      },
      errors: {
        password: "",
        validation: {},
        unknown: "",
      },
      allProjects: [],
      projectsLoading: false,
      dataTableHeight: dataTableHeight,
      canChangeTenantAdminField: false,
      icons: {
        yes: "mdi-checkbox-outline",
        no: "mdi-checkbox-blank-outline",
      },
    }
  },
  computed: {
    ...mapGetters(['selectedTenant', 'selectedTenantObj', 'profile', "employee", "isSuperuser", "userid"]),
    dialogWidth: dialogWidth,
    allEmployees () {
      return [{
        label: "Alle Mitarbeiter",
        values: this.items
      }]
    },
    selectedEmployeeId () {
      return this.$route.params.id || null
    },
    itemFilters () {
      const r = {
        tenants: this.selectedTenant,
        details: true,
        ordering: "last_name",
      }
      if (!this.profile.show_inactive_employees) {
        r.inactive = false
      }
      return r
    },
    projectFilters () {
      const r = {
        tenant: this.selectedTenant,
        ordering: "name",
      }
      if (!this.profile.show_inactive_projects) {
        r.inactive = false
      }
      return r
    },
    ignore_project_operation_assignment () {
      return this.selectedTenantObj && this.selectedTenantObj.ignore_project_operation_assignment
    },
  },
  watch: {
    selectedTenant: {
      handler (new_, old) {
        this.fetchItems()
        this.fetchProjects()
      },
      immediate: true,
    },
    'profile.show_inactive_employees' () {
      this.fetchItems()
    },
    'profile.show_inactive_projects' () {
      this.fetchProjects()
    },
    selectedEmployeeId: {
      async handler (newValue) {
        if (newValue === null) {
          this.showEditDialog = false
          return
        }
        if (newValue === "new") {
          this.selectedObject.id = null
          this.selectedObject.inactive = false
          this.selectedObject.changePassword = true
          this.selectedObject.user.pk = null
          this.selectedObject.user.username = ""
          this.selectedObject.user.first_name = ""
          this.selectedObject.user.last_name = ""
          this.selectedObject.user.email = ""
          this.selectedObject.user.password = ""
          this.selectedObject.user.password2 = ""
          this.selectedObject.projects.splice(0)
          this.selectedObject.manages_employees.splice(0)
          this.selectedObject.group_leader = false
          this.selectedObject.is_tenant_admin = false
          this.errors.password = ""
          Vue.set(this.errors, "validation", {})
          this.showEditDialog = true
        } else {
          this.loading = true
          try {
            const r = await api(endpoints.fetch_employee(this.selectedEmployeeId))
            if (r.ok) {
              const employee = await r.json()
              this.selectedObject.id = employee.id
              this.selectedObject.user.first_name = employee.user.first_name
              this.selectedObject.user.last_name = employee.user.last_name
              this.selectedObject.inactive = employee.inactive
              this.selectedObject.changePassword = false
              this.selectedObject.user.pk = employee.user.pk
              this.selectedObject.user.username = employee.user.username
              this.selectedObject.user.email = employee.user.email
              this.selectedObject.user.password = ""
              this.selectedObject.user.password2 = ""
              this.selectedObject.projects.splice(0, this.selectedObject.projects.length, ...employee.projects)
              this.selectedObject.manages_employees.splice(0, this.selectedObject.manages_employees.length, ...employee.manages_employees)
              this.selectedObject.permissions = employee.permissions
              this.selectedObject.is_tenant_admin = this.isTenantAdmin(employee, this.selectedTenant),
              this.selectedObject.group_leader = this.isGroupLeader(employee)
              this.canChangeTenantAdminField = (this.isTenantAdmin(this.employee, this.selectedTenant) || this.isSuperuser) && employee.user.pk !== this.userid
              this.errors.password = ""
              Vue.set(this.errors, "validation", {})
              this.showEditDialog = true
            }
          } catch (err) {
            // TODO: Error handling
          } finally {
            this.loading = false
          }
        }
      },
      immediate: true,
    },
  },
  methods: {
    async fetchItems () {
      this.loading = true
      this.errors.unknown = ""
      if (this.selectedTenant === null) {
        return
      }
      try {
        const r = await api(endpoints.fetch_employee(), this.itemFilters, {method: 'GET'})
        if (r.ok) {
          const d = await r.json()
          this.items.splice(0, this.items.length, ...d)
        } else {
          this.items.splice(0)
          this.errors.unknown = (await r.json()).detail
        }
      } catch (err) {
        console.log(err)
        this.items.splice(0)
        this.errors.unknown = err.message
      } finally {
        this.loading = false
      }
    },
    openNewDialog () {
      this.$router.push({name: "employees", params: {id: "new"}})
    },
    openEditDialog (employee) {
      this.$router.push({name: "employees", params: {id: employee.id}})
    },
    openDeleteDialog (employee) {
      this.selectedObject.id = employee.id
      this.selectedObject.user.first_name = employee.user.first_name
      this.selectedObject.user.last_name = employee.user.last_name
      this.showDeleteDialog = true
    },
    formIsValid () {
      const passwordIsValid = !this.selectedObject.changePassword || (this.selectedObject.user.password !== "" && this.selectedObject.user.password === this.selectedObject.user.password2)
      if (!passwordIsValid) {
        this.errors.password = "Die angegebenen Kennwörter stimmen nicht überein."
      } else {
        this.errors.password = ""
      }
      return passwordIsValid
    },
    async saveEditDialog () {
      if (!this.formIsValid()) {
        return
      }
      this.saving = true
      const data = {
        inactive: this.selectedObject.inactive,
        user: {
          pk: this.selectedObject.user.pk,
          username: this.selectedObject.user.username,
          first_name: this.selectedObject.user.first_name,
          last_name: this.selectedObject.user.last_name,
          email: this.selectedObject.user.email,
        },
        projects: this.selectedObject.projects,
        manages_employees: this.selectedObject.manages_employees,
      }
      if (this.selectedObject.changePassword) {
        data.user.password = this.selectedObject.user.password
      }
      const isNewObject = this.selectedObject.id === null
      if (isNewObject) {
        data.tenants = [this.selectedTenant]
      }

      if (this.canChangeTenantAdminField){
        data.is_tenant_admin = this.selectedObject.is_tenant_admin,
        data.tenant_id = this.selectedTenant
      }

      try {
        const r =
          !isNewObject ?
          await api(endpoints.update_employee(this.selectedObject.id), data, {method: "PATCH"}) :
          await api(endpoints.create_employee(), data, {method: "POST"})
        if (r.ok) {
          this.$router.push({name: "employees"})
           this.$root.$emit("employeesChanged")
          await this.fetchItems()
        } else {
          // TODO: error handling
          Vue.set(this.errors, "validation", await r.json())
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    async deleteEmployee () {
      this.saving = true
      try {
        const r = await api(endpoints.delete_employee(this.selectedObject.id), undefined, {method: "DELETE"})
        if (r.ok) {
          this.showDeleteDialog = false
          this.$root.$emit("employeesChanged")
          await this.fetchItems()
        } else {
          // TODO: error handling
          Vue.set(this.errors, "validation", await r.json())
        }
      } catch (err) {
        // TODO: error handling
      } finally {
        this.saving = false
      }
    },
    getError (path) {
      const parts = path.split(".")
      let obj = this.errors.validation
      while (parts.length > 0) {
        const key = parts.splice(0, 1)
        obj = obj[key]
        if (typeof obj === "undefined") {
          return obj
        }
      }
      return obj
    },
    isTenantAdmin (employee, tenantId) {
      return employee.permissions.some(i => i.permission_type === "admin" && i.object_type === "tenant" && i.object_pk === tenantId)
    },
    isGroupLeader (employee) {
      return employee.manages_employees.length > 0
    },
    async fetchProjects () {
      if (this.selectedTenant === null) {
        return
      }
      this.projectsLoading = true
      try {
        const r = await api(endpoints.fetch_project(), this.projectFilters, {method: "GET"})
        if (r.ok) {
          const d = await r.json()
          this.allProjects.splice(0, this.allProjects.length, ...d)
        } else {
          // TODO: error handling
        }
      } catch (err) {
          // TODO: error handling
      } finally {
        this.projectsLoading = false
      }
    },
    getEmployeeLabel: getEmployeeLabel
  },
}
</script>

<style>
</style>
